import { useParams, useNavigate } from 'react-router';
import { useOrder, useUpdateOrder, useProjects } from '@t90/api-client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  ArrowLeft,
  Edit,
  Loader2,
} from '@t90/ui';
import { formatDate, formatCurrency } from '@t90/common';

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: order, isLoading, error } = useOrder(id!);
  const { data: projects } = useProjects({ orderId: id });

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex h-96 flex-col items-center justify-center">
        <p className="text-lg font-medium">Order not found</p>
        <p className="text-sm text-muted-foreground">
          The order you're looking for doesn't exist or has been removed.
        </p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/orders')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Orders
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold">{order.order_number}</h1>
            <Badge variant={getStatusVariant(order.order_status)}>{order.order_status}</Badge>
          </div>
          <p className="text-muted-foreground">
            {order.customer?.customer_name ?? 'Unknown Customer'}
          </p>
        </div>
        <Button onClick={() => navigate(`/orders/${id}/edit`)}>
          <Edit className="mr-2 h-4 w-4" />
          Edit Order
        </Button>
      </div>

      {/* Content */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="projects">Projects ({projects?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Order Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InfoRow label="Order Number" value={order.order_number} />
                <InfoRow label="Status" value={order.order_status} />
                <InfoRow label="Location" value={order.location?.location_name ?? '-'} />
                <InfoRow label="Priority" value={order.priority} />
                <InfoRow label="PO Number" value={order.po_number ?? '-'} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Dates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <InfoRow label="Order Date" value={formatDate(order.order_date)} />
                <InfoRow
                  label="Due Date"
                  value={order.due_date ? formatDate(order.due_date) : '-'}
                />
                <InfoRow label="Created" value={formatDate(order.created_at)} />
                <InfoRow label="Updated" value={formatDate(order.updated_at)} />
              </CardContent>
            </Card>
          </div>

          {order.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{order.notes}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="projects" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Associated Projects</CardTitle>
              <CardDescription>Projects created for this order</CardDescription>
            </CardHeader>
            <CardContent>
              {projects?.length ? (
                <div className="space-y-4">
                  {projects.map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between rounded-lg border p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <div>
                        <p className="font-medium">{project.project_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {(project as any).service_item?.service_item_name ?? 'Unknown'}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant={getProjectStatusVariant(project.project_status)}>
                          {project.project_status}
                        </Badge>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {project.units_completed?.toLocaleString() ?? 0} /{' '}
                          {project.quantity?.toLocaleString() ?? 0} units
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No projects associated with this order yet.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Activity History</CardTitle>
              <CardDescription>Timeline of changes and updates</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Activity history coming soon...
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'New':
      return 'secondary';
    case 'In Progress':
      return 'default';
    case 'Completed':
      return 'success';
    case 'Cancelled':
      return 'destructive';
    case 'On Hold':
      return 'warning';
    default:
      return 'secondary';
  }
}

function getProjectStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'Pending':
      return 'secondary';
    case 'In Progress':
      return 'default';
    case 'Completed':
      return 'success';
    case 'On Hold':
      return 'warning';
    default:
      return 'secondary';
  }
}
